/**
 * Lightweight ECS.
 *
 * An Entity is an integer id. A Component is a plain object stored in a
 * `Map<EntityId, ComponentData>` keyed by component type. Systems query the
 * world for entities that have all of a given set of components.
 *
 * To add a new component:
 *   1. Define its interface in /src/components/<Name>.ts and export a unique
 *      `<name>Type: ComponentType<T>` constant (just a string brand).
 *   2. `world.addComponent(entity, <name>Type, data)` from a factory.
 *   3. Query with `world.query([<name>Type, ...])` in a system.
 *
 * Intentionally minimal: no archetypes, no dense arrays — hundreds of
 * entities easily fit in a Map-based store, and it keeps the code readable.
 */

export type EntityId = number;

export interface ComponentType<T> {
  readonly id: string;
  readonly __brand?: T; // phantom type, never actually used
}

export function defineComponent<T>(id: string): ComponentType<T> {
  return { id } as ComponentType<T>;
}

export class World {
  private nextId = 1;
  private alive = new Set<EntityId>();
  /** componentId -> (entityId -> componentData) */
  private stores = new Map<string, Map<EntityId, unknown>>();
  /** entityId -> Set<componentId> for O(1) has() */
  private entityComponents = new Map<EntityId, Set<string>>();

  createEntity(): EntityId {
    const id = this.nextId++;
    this.alive.add(id);
    this.entityComponents.set(id, new Set());
    return id;
  }

  destroyEntity(e: EntityId): void {
    if (!this.alive.has(e)) return;
    const comps = this.entityComponents.get(e);
    if (comps) {
      for (const cid of comps) this.stores.get(cid)?.delete(e);
    }
    this.entityComponents.delete(e);
    this.alive.delete(e);
  }

  exists(e: EntityId): boolean { return this.alive.has(e); }

  addComponent<T>(e: EntityId, type: ComponentType<T>, data: T): T {
    if (!this.alive.has(e)) throw new Error(`addComponent: entity ${e} does not exist`);
    let store = this.stores.get(type.id);
    if (!store) { store = new Map(); this.stores.set(type.id, store); }
    store.set(e, data);
    this.entityComponents.get(e)!.add(type.id);
    return data;
  }

  removeComponent<T>(e: EntityId, type: ComponentType<T>): void {
    this.stores.get(type.id)?.delete(e);
    this.entityComponents.get(e)?.delete(type.id);
  }

  has<T>(e: EntityId, type: ComponentType<T>): boolean {
    return this.entityComponents.get(e)?.has(type.id) ?? false;
  }

  get<T>(e: EntityId, type: ComponentType<T>): T | undefined {
    return this.stores.get(type.id)?.get(e) as T | undefined;
  }

  getRequired<T>(e: EntityId, type: ComponentType<T>): T {
    const v = this.get(e, type);
    if (v === undefined) throw new Error(`getRequired: entity ${e} missing component ${type.id}`);
    return v;
  }

  /** Iterate all entities that have every component in `types`. */
  *query(types: ComponentType<unknown>[]): IterableIterator<EntityId> {
    if (types.length === 0) { yield* this.alive; return; }
    // pick the smallest store to iterate as the outer loop
    let smallestStore: Map<EntityId, unknown> | undefined;
    for (const t of types) {
      const s = this.stores.get(t.id);
      if (!s || s.size === 0) return;
      if (!smallestStore || s.size < smallestStore.size) smallestStore = s;
    }
    outer: for (const e of smallestStore!.keys()) {
      const set = this.entityComponents.get(e);
      if (!set) continue;
      for (const t of types) if (!set.has(t.id)) continue outer;
      yield e;
    }
  }

  /** Clear the entire world (used on stage transitions). */
  clear(): void {
    this.alive.clear();
    this.stores.clear();
    this.entityComponents.clear();
    this.nextId = 1;
  }

  /** Snapshot used by debug tooling/HUD. */
  stats(): { entities: number; components: number } {
    let components = 0;
    for (const s of this.stores.values()) components += s.size;
    return { entities: this.alive.size, components };
  }
}
